import React, { useContext, useCallback, FunctionComponent } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { ControllerRenderProps, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { UPSERT_CUSTOMERSETTING } from "src/api/customer";
import { UpsertCustomersetting, UpsertCustomersettingVariables } from "src/api/generated/UpsertCustomersetting";
import { MessageType } from "src/components/notifications/Notifier";
import { ums_customersettings_insert_input } from "src/api/generated/globalTypes";
import styles from "./VisaworkflowSettingsForm.module.css";
import { Controller } from "react-hook-form";
import { GetKrediflowSettings as KrediflowSettings } from "src/api/generated/GetKrediflowSettings";
import { Link } from "react-router-dom";
import { Route } from "src/config/routes";

type VisaworkflowEmailSettingsFormProps = {
    krediflowSettings: KrediflowSettings;
    refetchKrediflowSettings: any;
};

interface VisaworkflowSettingsFormFields {
    manualVisaworkflowDefinition: boolean;
}

const VisaworkflowSettingsFormBase: FunctionComponent<VisaworkflowEmailSettingsFormProps> = (props) => {
    const { t } = useTranslation();

    const { uiStore } = useContext(RootStoreContext);

    const { refetchKrediflowSettings, krediflowSettings } = props;

    const [upsertCustomersettingMutation, { loading: isUpsertCustomersettingMutationLoading }] = useMutation<
        UpsertCustomersetting,
        UpsertCustomersettingVariables
    >(UPSERT_CUSTOMERSETTING, {
        onCompleted: () => {
            uiStore.printStatusMessage(t("screens.easycontact_settings.success_save_message"), MessageType.SUCCESS);
        }
    });

    const manualVisaworkflowDefinition = krediflowSettings.visaworkflow[0]?.value
        ? krediflowSettings.visaworkflow[0]?.value === "true"
            ? true
            : false
        : false;

    const { handleSubmit, formState, reset, control } = useForm<VisaworkflowSettingsFormFields>({
        mode: "onBlur",
        defaultValues: {
            manualVisaworkflowDefinition: manualVisaworkflowDefinition ?? false
        }
    });

    const resetForm = useCallback(() => {
        const manualVisaworkflowDefinition = krediflowSettings.visaworkflow[0].value;

        reset({
            manualVisaworkflowDefinition: manualVisaworkflowDefinition === "true" ? true : false
        });
    }, [reset, krediflowSettings]);

    const { dirtyFields } = formState;

    const handleFormSubmit = useCallback(
        async (formData: VisaworkflowSettingsFormFields) => {
            try {
                const insertInput: ums_customersettings_insert_input[] = [];

                if (dirtyFields.manualVisaworkflowDefinition) {
                    insertInput.push({
                        key: "KREDIFLOW.MANUAL_VISAWORKFLOW_DEFINITION",
                        value: formData.manualVisaworkflowDefinition.toString(),
                        valuetype: 1
                    });
                }

                await upsertCustomersettingMutation({
                    variables: {
                        objects: insertInput
                    }
                });

                refetchKrediflowSettings();

                reset(
                    { ...formData },
                    {
                        keepDirty: false,
                        keepDefaultValues: false,
                        keepValues: false
                    }
                );
            } catch (error: any) {
                uiStore.printStatusMessage(
                    `${t("screens.easycontact_settings.failure_save_message")}: ${error.message}`,
                    MessageType.ERROR
                );
            }
        },
        [reset, upsertCustomersettingMutation, uiStore, t, dirtyFields, refetchKrediflowSettings]
    );

    const renderManualVisaworkflowDefinitionSwitch = useCallback(
        ({
            field: { onChange, onBlur, value, name, ref }
        }: {
            field: ControllerRenderProps<VisaworkflowSettingsFormFields, "manualVisaworkflowDefinition">;
        }) => {
            return <UI.Switch checked={value} onToggle={onChange} />;
        },
        []
    );

    const linkToRealestateVisas = (
        <Link to={Route.krediflowSettingsRealestateVisas}>
            {t("screens.kredi_flow_settings.visaworkflow.customize").toString()}
        </Link>
    );

    const manualVisaworkflowDefinitionSwitch = (
        <div className="d-flex align-items-center" style={{ height: 25 }}>
            <Controller
                name="manualVisaworkflowDefinition"
                control={control}
                render={renderManualVisaworkflowDefinitionSwitch}
            />
            <div className="ml-auto">{manualVisaworkflowDefinition ? linkToRealestateVisas : ""}</div>
        </div>
    );

    return (
        <UI.Form>
            {manualVisaworkflowDefinitionSwitch}

            <div className="mt-4">
                <UI.Button
                    type="submit"
                    label={isUpsertCustomersettingMutationLoading ? t("labels.is_saving") : t("labels.save")}
                    disabled={!formState.isDirty}
                    onClick={handleSubmit(handleFormSubmit)}
                    className={styles.FormButton}
                />
                <UI.Button
                    label={t("labels.reset")}
                    disabled={!formState.isDirty}
                    onClick={resetForm}
                    className={`${styles.FormButton} ml-sm-2 mt-2 mt-sm-0`}
                />
            </div>
        </UI.Form>
    );
};

export const VisaworkflowSettingsForm = Sentry.withProfiler(VisaworkflowSettingsFormBase);
